import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import {
  House, ChartLine, Flask, Package, Truck, Factory, ClipboardText,
  Snowflake, ShieldCheck, ShoppingCart, Users, Barcode, ChartBar, SignOut,
  Kanban, Trophy, ChartPie, Sun, Moon, Clock, Gear, ChefHat, Buildings, ListChecks
} from "@phosphor-icons/react";

const NAV = [
  { section: "Overview", items: [
    { to: "/", label: "Command Centre", icon: House, testid: "nav-dashboard" },
    { to: "/management", label: "Management", icon: Buildings, testid: "nav-management" },
    { to: "/kanban", label: "Live Workflow", icon: Kanban, testid: "nav-kanban" },
    { to: "/planning", label: "Smart Planner", icon: ChartLine, testid: "nav-planning" },
    { to: "/analytics", label: "Analytics", icon: ChartPie, testid: "nav-analytics" },
  ]},
  { section: "Manufacturing", items: [
    { to: "/batches", label: "Production Batches", icon: Factory, testid: "nav-batches" },
    { to: "/kitchen", label: "Kitchen SOPs", icon: ChefHat, testid: "nav-kitchen" },
    { to: "/machines", label: "Freeze Dryers", icon: Snowflake, testid: "nav-machines" },
    { to: "/qc", label: "Quality Control", icon: ShieldCheck, testid: "nav-qc" },
    { to: "/skus", label: "SKUs & Recipes", icon: Flask, testid: "nav-skus" },
  ]},
  { section: "Supply Chain", items: [
    { to: "/materials", label: "Raw Materials", icon: Package, testid: "nav-materials" },
    { to: "/procurement", label: "Procurement", icon: ShoppingCart, testid: "nav-procurement" },
    { to: "/finished-goods", label: "Finished Goods", icon: ClipboardText, testid: "nav-fg" },
    { to: "/dispatch", label: "Dispatch", icon: Truck, testid: "nav-dispatch" },
  ]},
  { section: "Intelligence", items: [
    { to: "/trace", label: "Traceability", icon: Barcode, testid: "nav-trace" },
    { to: "/operators", label: "Operator Perf", icon: Trophy, testid: "nav-operators" },
    { to: "/attendance", label: "Attendance", icon: Clock, testid: "nav-attendance" },
    { to: "/reports", label: "Reports", icon: ChartBar, testid: "nav-reports" },
    { to: "/users", label: "Users", icon: Users, testid: "nav-users", adminOnly: true },
    { to: "/audit", label: "Audit Log", icon: ListChecks, testid: "nav-audit", adminOnly: true },
    { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings", adminOnly: true },
  ]},
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("chericious_theme") === "dark";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("chericious_theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      <aside className="w-full md:w-64 md:h-screen md:sticky md:top-0 border-r border-border bg-card flex flex-col">
        <div className="h-16 border-b border-border flex items-center px-5 gap-3">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <Snowflake weight="fill" size={20} className="text-primary-foreground"/>
          </div>
          <div className="leading-tight">
            <div className="font-display font-black text-[15px] tracking-tight">CHERICIOUS</div>
            <div className="overline text-[9px]">MES · Freeze-Dried</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
          {NAV.map(sec => (
            <div key={sec.section}>
              <div className="overline text-[9px] px-3 mb-1.5">{sec.section}</div>
              {sec.items.filter(n => !n.adminOnly || user?.role === "admin").map(n => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`
                  }
                >
                  <n.icon size={18} weight="duotone"/>
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0 leading-tight">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{user?.role?.replace("_", " ")}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-btn"
          >
            <SignOut size={16}/> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-20 flex items-center px-6 justify-between">
          <div>
            <div className="overline">Chericious Foods · Ahmedabad</div>
            <div className="text-sm font-semibold">Manufacturing Execution System · Single Shift 6 AM – 6 PM</div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={()=>setDark(!dark)} data-testid="theme-toggle">
              {dark ? <Sun size={20}/> : <Moon size={20}/>}
            </Button>
            <NotificationBell/>
          </div>
        </header>
        <main className="flex-1 p-6 md:p-8">{children}</main>
      </div>
    </div>
  );
}
